import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FixMeLater } from 'types'
import { useAppSelector } from 'app/hooks'
import useInfoModal from 'utils/hooks/useInfoModal'
import { getObjectDiff } from 'utils/helpers/objectDiffHelper'
import { BUTTONS, ERRORS, MODALS, TABS } from 'locales/locales'
import { useLazyGetUserByIdQuery, useUpdateUserByIdMutation } from 'app/services/user.service'

import UserInfo from './UserInfoForm'
import CustomModal from 'components/modals/CustomModal'
import UserStatistic from 'components/user-statistic/UserStatistics'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { IRolePermissions, UserManagementPermissions } from 'types/role.types'

interface IUserInfoProps {
  userId?: string
  onClose: () => void
  fetchUsers: () => void
}
const initialUserState: {
  [key: string]: FixMeLater
} = {
  avatar: '',
  fullname: '',
  email: '',
  title: '',
  isDeleted: false,
  isAdmin: false,
}
const statisticDataInitial = {
  totalChannels: 0,
  totalMessages: 0,
  totalFiles: 0,
  totalVideoCall: 0,
}
const UserInfoModal = ({ userId, onClose, fetchUsers }: IUserInfoProps) => {
  const { t } = useTranslation()
  const { openModal, closeModal, Modal } = useInfoModal()
  const [formChecked, setFormChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentUserData, setCurrentUserData] = useState({})

  const [selectedOption, setSelectedOption] = useState({})
  const [selectedUserData, setSelectedUserData] = useState(initialUserState)
  const [userStatisticData, setUserStatisticData] = useState(statisticDataInitial)

  const [updateUser] = useUpdateUserByIdMutation()
  const [getUserById] = useLazyGetUserByIdQuery()

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasUserViewStatPermission = isFlagSetIn([UserManagementPermissions.VIEW_USER_STATISTICS], permissions.userManagement)

  useEffect(() => {
    fetchChannelInfo()
  }, [])

  const fetchChannelInfo = async () => {
    try {
      const result: FixMeLater = await getUserById({ id: userId })
      setSelectedUserData(result.data.data.info)
      setCurrentUserData(result.data.data.info)
      setUserStatisticData(result.data.data.statistic)
    } catch {
      onClose()
    } finally {
      setIsLoading(false)
    }
  }
  const onConfirmOk = async () => {
    setFormChecked(true)
    GenerateDiffObject.then(async (value) => {
      try {
        setIsLoading(true)
        await updateUser({ _id: userId, params: value })
        fetchUsers()
      } finally {
        setFormChecked(false)
        setIsLoading(false)
      }
    })
      .catch((error) => {
        DropdownAlert.ref.show({ isError: true, message: t(ERRORS.THERE_WAS_NO_CHANGES) || error })
      })
      .finally(() => handleClose())
  }

  const GenerateDiffObject = new Promise(function (myResolve, myReject) {
    try {
      let data = {}
      const diffrence = getObjectDiff(currentUserData, selectedUserData)
      if (diffrence.length != 0) {
        diffrence.map((key: string) => {
          data = { ...data, [key]: selectedUserData[key].value || selectedUserData[key] }
        })
        myResolve(data)
      }
    } catch (error) {
      myReject(error)
    }
  })
  const onConfirmCancel = () => {
    closeModal()
  }

  const onOk = () => {
    openModal({
      title: t(MODALS.UPDATE_USER_TITLE),
      description: t(MODALS.UPDATE_USER_DESCRIPTION),
      cancelTitle: t(BUTTONS.CANCEL),
      okTitle: t(BUTTONS.YES),
      onCancel: onConfirmCancel,
      onOk: onConfirmOk,
    })
  }

  const onChangeInput = (value: string | boolean, key: string) => {
    if (key === 'teamId') setSelectedOption(value)
    setSelectedUserData({ ...selectedUserData, [key]: value })
  }

  const modalTabs: FixMeLater = [{ title: t(TABS.GENERAL), action: true }]

  if (hasUserViewStatPermission) {
    modalTabs.push({ title: t(TABS.STATISTIC) })
  }
 
  const handleClose = () => {
    onClose()
    setSelectedUserData(initialUserState)
    closeModal()
  }
  return (
    <>
      <CustomModal
        closeModal={onClose}
        onCancel={onClose}
        onOk={onOk}
        title={t(MODALS.USER_INFO_TITLE)}
        tab={modalTabs}
        showButtons={false}
        loading={isLoading}
      >
        <UserInfo
          id={selectedUserData._id}
          avatar={selectedUserData.avatar}
          fullname={selectedUserData.fullname}
          email={selectedUserData.email}
          title={selectedUserData.title}
          formChecked={formChecked}
          isDeleted={selectedUserData.isDeleted}
          selectedOption={selectedOption}
          onChange={onChangeInput}
        />
        <UserStatistic {...userStatisticData} /> 
      </CustomModal>
      {Modal}
    </>
  )
}

export default UserInfoModal
